import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { badge, box } from '../shared/styles';
import Form from './Form';

/*
 * The equipment: register it, schedule its calibration and maintenance, log
 * what was done (and what it cost), set the limits of what it monitors and
 * record readings against them. Out-of-spec readings open an excursion.
 */
const today = () => new Date().toISOString().slice(0, 10);
const TONE = { in_service: 'success', out_of_service: 'danger', quarantined: 'warning', retired: 'info' };

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function InstrumentDetail({ id, onChanged, t }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const manage = can(user, 'iso:manage');
  const observe = can(user, 'lab:observe');
  const load = useCallback(() => api.get(`/iso/instruments/${id}`).then(({ data }) => setDetail(data))
    .catch((e) => setError(errorText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const post = (path) => async (body) => { await api.post(`/iso/instruments/${id}${path}`, body); await load(); onChanged(); };

  if (!detail) return error ? <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p> : <p>{t('loading')}</p>;
  return (
    <div data-testid="instrument-detail">
      <h3 style={{ marginTop: 0 }}>{detail.asset_tag} · {detail.name}
        <span style={badge(TONE[detail.status])} data-testid="instrument-status">{t(`status_${detail.status}`)}</span></h3>
      <p style={{ fontSize: 13 }}>{[detail.manufacturer, detail.model, detail.serial_no, detail.location].filter(Boolean).join(' · ')}</p>

      {detail.excursions.filter((e) => !e.closed_at).map((e) => (
        <p key={e.excursion_id} role="alert" style={{ color: 'var(--c-danger-fg)' }} data-testid="open-excursion">
          {t('excursionOpen', { parameter: e.parameter, worst: e.worst_value, n: e.readings })}
          {e.nc_id && ` · ${t('ncRaised')}`}
        </p>
      ))}

      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('plans')}</h4>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>{detail.plans.map((p) => (
            <tr key={p.plan_id}><td>{t(`opt_${p.kind}`)}</td><td>{p.description}</td>
              <td>{t('everyDays', { n: p.interval_days })}</td>
              <td>{p.cost_per_event ? `${p.cost_per_event} ${p.currency}` : t('noCost')}</td>
              <td style={{ color: p.next_due < today() ? 'var(--c-danger-fg)' : undefined }}>{t('due')} {p.next_due}</td></tr>
          ))}</tbody>
        </table>
        {manage && (
          <Form t={t} testId="plan-form" submitLabel="addPlan" submitTip="tip.addPlan" onSubmit={(v) => post('/plans')({
            ...v, interval_days: Number(v.interval_days), cost_per_event: v.cost_per_event || null })}
          fields={[{ name: 'kind', options: ['calibration', 'preventive', 'verification'], initial: 'calibration', tip: 'tip.planKind' },
            { name: 'description', tip: 'tip.planDescription' }, { name: 'interval_days', type: 'number', initial: '365', size: 5, tip: 'tip.intervalDays' },
            { name: 'cost_per_event', size: 7, tip: 'tip.costPerEvent' }, { name: 'currency', initial: 'ILS', size: 4, tip: 'tip.currency' },
            { name: 'next_due', type: 'date', tip: 'tip.nextDue' }, { name: 'reason', tip: 'tip.reason' }]} />
        )}
      </section>

      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('maintenance')}</h4>
        {observe && (
          <Form t={t} testId="event-form" submitLabel="logEvent" submitTip="tip.logEvent" onSubmit={(v) => post('/maintenance')({
            ...v, plan_id: v.plan_id || null, cost: v.cost || null })}
          fields={[{ name: 'kind', options: ['calibration', 'preventive', 'verification', 'repair'], initial: 'preventive', tip: 'tip.eventKind' },
            { name: 'plan_id', label: 'plan', initial: '', tip: 'tip.eventPlan',
              options: [{ value: '', label: '—' }, ...detail.plans.map((p) => ({ value: p.plan_id, label: p.description }))] },
            { name: 'performed_on', type: 'date', initial: today(), tip: 'tip.performedOn' }, { name: 'performed_by', tip: 'tip.performedBy' },
            { name: 'result', options: ['pass', 'fail', 'adjusted', 'done'], initial: 'done', tip: 'tip.result' },
            { name: 'cost', size: 7, tip: 'tip.cost' }, { name: 'reason', tip: 'tip.reason' }]} />
        )}
        <ul style={{ fontSize: 13 }}>{detail.events.map((e) => (
          <li key={e.event_id}>{e.performed_on} · {t(`opt_${e.kind}`)} · {t(`opt_${e.result}`)} · {e.performed_by}
            {e.cost && ` · ${e.cost} ${e.currency}`}</li>
        ))}</ul>
      </section>

      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('parameters')}</h4>
        <ul style={{ fontSize: 13 }}>{detail.parameters.map((p) => (
          <li key={p.parameter_id}>{p.label} ({p.key}): [{p.lower_limit ?? '−∞'}, {p.upper_limit ?? '∞'}] {p.unit || ''}
            {p.critical && <span style={badge('danger')}>{t('critical')}</span>}</li>
        ))}</ul>
        {manage && (
          <Form t={t} testId="parameter-form" submitLabel="addParameter" submitTip="tip.addParameter" onSubmit={(v) => post('/parameters')({
            ...v, lower_limit: num(v.lower_limit), upper_limit: num(v.upper_limit) })}
          fields={[{ name: 'key', size: 12, tip: 'tip.paramKey' }, { name: 'label', tip: 'tip.paramLabel' }, { name: 'unit', size: 4, tip: 'tip.unit' },
            { name: 'lower_limit', size: 6, tip: 'tip.lowerLimit' }, { name: 'upper_limit', size: 6, tip: 'tip.upperLimit' },
            { name: 'critical', type: 'checkbox', initial: false, tip: 'tip.critical' }, { name: 'reason', tip: 'tip.reason' }]} />
        )}
        {observe && detail.parameters.length > 0 && (
          <Form t={t} testId="reading-form" submitLabel="addReading" submitTip="tip.addReading"
            onSubmit={(v) => post('/readings')({ parameter: v.parameter, value: Number(v.value) })}
            fields={[{ name: 'parameter', initial: detail.parameters[0].key, tip: 'tip.readingParameter',
              options: detail.parameters.map((p) => ({ value: p.key, label: p.label })) },
              { name: 'value', size: 7, tip: 'tip.readingValue' }]} />
        )}
        <ul style={{ fontSize: 13 }}>{detail.readings.slice(0, 10).map((r, i) => (
          <li key={i} style={{ color: r.within_spec ? undefined : 'var(--c-danger-fg)' }}>
            {new Date(r.measured_at).toLocaleString()} · {r.parameter} = {r.value} {r.within_spec ? '✓' : '✗'}</li>
        ))}</ul>
      </section>

      {manage && detail.status !== 'retired' && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('changeStatus')}</h4>
          <Form t={t} testId="status-form" submitLabel="save" submitTip="tip.saveStatus" onSubmit={post('/status')}
            fields={[{ name: 'status', options: ['in_service', 'out_of_service', 'quarantined', 'retired'],
              initial: detail.status, tip: 'tip.status' }, { name: 'reason', tip: 'tip.reason' }]} />
        </section>
      )}
    </div>
  );
}

export default function Instruments() {
  const { t } = useTranslation('quality');
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api.get('/iso/instruments').then(({ data }) => setList(data))
    .catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const register = async (v) => {
    const { data } = await api.post('/iso/instruments', v);
    await load();
    setSelected(data.instrument_id);
  };
  return (
    <div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {can(user, 'iso:manage') && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('register')}</h4>
          <Form t={t} testId="register-form" submitLabel="register" submitTip="tip.register" onSubmit={register}
            fields={[{ name: 'name', tip: 'tip.instrumentName' }, { name: 'category', initial: 'general', size: 10, tip: 'tip.category' },
              { name: 'manufacturer', size: 10, tip: 'tip.manufacturer' }, { name: 'model', size: 10, tip: 'tip.model' },
              { name: 'serial_no', size: 10, tip: 'tip.serialNo' }, { name: 'location', size: 10, tip: 'tip.location' },
              { name: 'reason', tip: 'tip.reason' }]} />
        </section>
      )}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <ul style={{ ...box, listStyle: 'none', minWidth: 240, flex: '0 1 300px', margin: 0 }}>
          {list.length === 0 && <li>{t('noInstruments')}</li>}
          {list.map((i) => (
            <li key={i.instrument_id}>
              <button type="button" data-testid={`instrument-${i.asset_tag}`} title={t('tip.openInstrument')} onClick={() => setSelected(i.instrument_id)}
                style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'start',
                  color: 'var(--c-text-fg)', width: '100%' }}>
                <strong>{i.asset_tag}</strong> {i.name}
                <span style={badge(TONE[i.status])}>{t(`status_${i.status}`)}</span>
                {i.open_excursions > 0 && <span style={badge('danger')}>{t('outOfSpec')}</span>}
                <br /><small style={{ color: 'var(--c-text-muted-fg)' }}>{i.next_due ? `${t('due')} ${i.next_due}` : t('noPlan')}</small>
              </button>
            </li>
          ))}
        </ul>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          {selected ? <InstrumentDetail key={selected} id={selected} onChanged={load} t={t} /> : <p>{t('choose')}</p>}
        </div>
      </div>
    </div>
  );
}

